import { supabase } from "./churchSupabaseClient";
import { fetchOrganizationAccess } from "./churchOrganizationAccessService";

const MENS_DUTIES_TABLE = "church_mens_duties";
const MENS_DUTY_TYPES_TABLE = "church_mens_duty_types";
const STORAGE_KEY = "oikos.church.mens-duties";
const TYPES_STORAGE_KEY = "oikos.church.mens-duty-types";

export const DEFAULT_MENS_DUTY_TYPES = [
  "Opening Prayer",
  "Song Leader",
  "Scripture Reading",
  "Table Talk",
  "Closing Prayer",
  "Announcements",
];

function isMissingRelationError(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes("could not find the table") ||
    message.includes("schema cache") ||
    message.includes("does not exist")
  );
}

function readLocalDuties() {
  if (typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.map(normalizeDuty) : [];
  } catch (_error) {
    return [];
  }
}

function saveLocalDuties(items = []) {
  if (typeof window === "undefined") return items;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (_error) {
    // Local cache is best effort.
  }

  return items;
}

function normalizeDutyType(row = {}, index = 0) {
  const name = typeof row === "string" ? row : row.name || row.duty_name || row.dutyName || "";

  return {
    id: typeof row === "object" && row.id ? row.id : `duty-type-${index + 1}`,
    accountId: typeof row === "object" ? row.account_id || row.accountId || "" : "",
    name,
    sortOrder: Number((typeof row === "object" ? row.sort_order ?? row.sortOrder : null) ?? index + 1),
    createdAt: typeof row === "object" ? row.created_at || row.createdAt || "" : "",
    updatedAt: typeof row === "object" ? row.updated_at || row.updatedAt || "" : "",
  };
}

function sortDutyTypes(items = []) {
  return [...items]
    .filter((item) => String(item?.name || "").trim())
    .sort((first, second) =>
      Number(first.sortOrder || 0) - Number(second.sortOrder || 0) ||
      String(first.name || "").localeCompare(String(second.name || ""))
    );
}

function readLocalDutyTypes() {
  if (typeof window === "undefined") {
    return DEFAULT_MENS_DUTY_TYPES.map(normalizeDutyType);
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(TYPES_STORAGE_KEY) || "[]");
    const source = Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_MENS_DUTY_TYPES;
    return sortDutyTypes(source.map(normalizeDutyType));
  } catch (_error) {
    return DEFAULT_MENS_DUTY_TYPES.map(normalizeDutyType);
  }
}

function saveLocalDutyTypes(items = []) {
  const nextItems = sortDutyTypes(items);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(TYPES_STORAGE_KEY, JSON.stringify(nextItems));
    } catch (_error) {
      // Local cache is best effort.
    }
  }
  return nextItems;
}

function normalizeDuty(row = {}) {
  return {
    id: row.id || `duty-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    accountId: row.account_id || row.accountId || "",
    dutyDate: row.duty_date || row.dutyDate || "",
    dutyName: row.duty_name || row.dutyName || "",
    assigneeName: row.assignee_name || row.assigneeName || "",
    note: row.note || "",
    showOnLive: row.show_on_live !== false && row.showOnLive !== false,
    createdAt: row.created_at || row.createdAt || "",
    updatedAt: row.updated_at || row.updatedAt || "",
  };
}

function sortDuties(items = []) {
  return [...items].sort((first, second) => {
    const dateCompare = String(first.dutyDate || "").localeCompare(String(second.dutyDate || ""));
    if (dateCompare !== 0) return dateCompare;
    return String(first.dutyName || "").localeCompare(String(second.dutyName || ""));
  });
}

async function getChurchAccountId(userId) {
  const access = await fetchOrganizationAccess(userId, "church");
  return access?.account?.id || null;
}

function validateDuty(values = {}) {
  const dutyDate = String(values.dutyDate || "").trim();
  const dutyName = String(values.dutyName || "").trim();
  const assigneeName = String(values.assigneeName || "").trim();

  if (!dutyDate) {
    throw new Error("Choose a Sunday.");
  }

  if (!dutyName) {
    throw new Error("Add a duty.");
  }

  if (!assigneeName) {
    throw new Error("Add a name.");
  }

  return {
    dutyDate,
    dutyName,
    assigneeName,
    note: String(values.note || "").trim(),
    showOnLive: values.showOnLive !== false,
  };
}

export async function loadMensDuties(userId) {
  if (!userId) {
    return readLocalDuties();
  }

  try {
    const accountId = await getChurchAccountId(userId);
    if (!accountId) return readLocalDuties();

    const { data, error } = await supabase
      .from(MENS_DUTIES_TABLE)
      .select("id, account_id, duty_date, duty_name, assignee_name, note, show_on_live, created_at, updated_at")
      .eq("account_id", accountId)
      .order("duty_date", { ascending: true })
      .order("duty_name", { ascending: true });

    if (error) {
      if (isMissingRelationError(error)) {
        return readLocalDuties();
      }
      throw error;
    }

    const duties = sortDuties((data || []).map(normalizeDuty));
    saveLocalDuties(duties);
    return duties;
  } catch (error) {
    if (isMissingRelationError(error)) {
      return readLocalDuties();
    }
    throw error;
  }
}

export async function loadMensDutiesForAccount(accountId) {
  if (!accountId) return [];

  const { data, error } = await supabase
    .from(MENS_DUTIES_TABLE)
    .select("id, account_id, duty_date, duty_name, assignee_name, note, show_on_live, created_at, updated_at")
    .eq("account_id", accountId)
    .order("duty_date", { ascending: true })
    .order("duty_name", { ascending: true });

  if (error) {
    if (isMissingRelationError(error)) return [];
    throw error;
  }

  return sortDuties((data || []).map(normalizeDuty));
}

export async function createPublicMensDuty(accountId, values = {}) {
  const nextDuty = validateDuty(values);

  if (!accountId) {
    throw new Error("Church account is missing.");
  }

  const { data, error } = await supabase
    .from(MENS_DUTIES_TABLE)
    .insert({
      account_id: accountId,
      duty_date: nextDuty.dutyDate,
      duty_name: nextDuty.dutyName,
      assignee_name: nextDuty.assigneeName,
      note: nextDuty.note,
      show_on_live: nextDuty.showOnLive,
    })
    .select("id, account_id, duty_date, duty_name, assignee_name, note, show_on_live, created_at, updated_at")
    .single();

  if (error) throw error;
  return normalizeDuty(data);
}

export async function createMensDuty(userId, values = {}) {
  const nextDuty = validateDuty(values);

  if (!userId) {
    const nextItems = sortDuties([...readLocalDuties(), normalizeDuty(nextDuty)]);
    saveLocalDuties(nextItems);
    return nextItems;
  }

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    const nextItems = sortDuties([...readLocalDuties(), normalizeDuty(nextDuty)]);
    saveLocalDuties(nextItems);
    return nextItems;
  }

  const payload = {
    account_id: accountId,
    duty_date: nextDuty.dutyDate,
    duty_name: nextDuty.dutyName,
    assignee_name: nextDuty.assigneeName,
    note: nextDuty.note,
    show_on_live: nextDuty.showOnLive,
  };

  const { error } = await supabase.from(MENS_DUTIES_TABLE).insert(payload);
  if (error) {
    if (isMissingRelationError(error)) {
      const nextItems = sortDuties([...readLocalDuties(), normalizeDuty(payload)]);
      saveLocalDuties(nextItems);
      return nextItems;
    }
    throw error;
  }

  return loadMensDuties(userId);
}

export async function updateMensDuty(userId, dutyId, values = {}) {
  const nextDuty = validateDuty(values);

  if (!userId || !dutyId) return readLocalDuties();

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    const nextItems = sortDuties(
      readLocalDuties().map((item) => (item.id === dutyId ? { ...item, ...nextDuty } : item))
    );
    saveLocalDuties(nextItems);
    return nextItems;
  }

  const { error } = await supabase
    .from(MENS_DUTIES_TABLE)
    .update({
      duty_date: nextDuty.dutyDate,
      duty_name: nextDuty.dutyName,
      assignee_name: nextDuty.assigneeName,
      note: nextDuty.note,
      show_on_live: nextDuty.showOnLive,
      updated_at: new Date().toISOString(),
    })
    .eq("account_id", accountId)
    .eq("id", dutyId);

  if (error) throw error;
  return loadMensDuties(userId);
}

export async function deleteMensDuty(userId, dutyId) {
  if (!dutyId) return readLocalDuties();

  if (!userId) {
    const nextItems = readLocalDuties().filter((item) => item.id !== dutyId);
    saveLocalDuties(nextItems);
    return nextItems;
  }

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    const nextItems = readLocalDuties().filter((item) => item.id !== dutyId);
    saveLocalDuties(nextItems);
    return nextItems;
  }

  const { error } = await supabase
    .from(MENS_DUTIES_TABLE)
    .delete()
    .eq("account_id", accountId)
    .eq("id", dutyId);

  if (error) throw error;
  return loadMensDuties(userId);
}

export async function toggleMensDutyLive(userId, duty = {}) {
  if (!duty?.id) return readLocalDuties();

  return updateMensDuty(userId, duty.id, {
    ...duty,
    showOnLive: duty.showOnLive === false,
  });
}

export async function loadMensDutyTypes(userId) {
  if (!userId) return readLocalDutyTypes();

  try {
    const accountId = await getChurchAccountId(userId);
    if (!accountId) return readLocalDutyTypes();

    const { data, error } = await supabase
      .from(MENS_DUTY_TYPES_TABLE)
      .select("id, account_id, name, sort_order, created_at, updated_at")
      .eq("account_id", accountId)
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      if (isMissingRelationError(error)) return readLocalDutyTypes();
      throw error;
    }

    if (!data?.length) {
      return seedDefaultMensDutyTypes(userId);
    }

    const dutyTypes = sortDutyTypes(data.map(normalizeDutyType));
    saveLocalDutyTypes(dutyTypes);
    return dutyTypes;
  } catch (error) {
    if (isMissingRelationError(error)) return readLocalDutyTypes();
    throw error;
  }
}

export async function loadMensDutyTypesForAccount(accountId) {
  if (!accountId) {
    return DEFAULT_MENS_DUTY_TYPES.map(normalizeDutyType);
  }

  const { data, error } = await supabase
    .from(MENS_DUTY_TYPES_TABLE)
    .select("id, account_id, name, sort_order, created_at, updated_at")
    .eq("account_id", accountId)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    if (isMissingRelationError(error)) {
      return DEFAULT_MENS_DUTY_TYPES.map(normalizeDutyType);
    }
    throw error;
  }

  return data?.length
    ? sortDutyTypes(data.map(normalizeDutyType))
    : DEFAULT_MENS_DUTY_TYPES.map(normalizeDutyType);
}

async function seedDefaultMensDutyTypes(userId) {
  const accountId = await getChurchAccountId(userId);
  if (!accountId) return readLocalDutyTypes();

  const payload = DEFAULT_MENS_DUTY_TYPES.map((name, index) => ({
    account_id: accountId,
    name,
    sort_order: index + 1,
  }));

  const { error } = await supabase.from(MENS_DUTY_TYPES_TABLE).insert(payload);
  if (error) {
    if (isMissingRelationError(error)) return readLocalDutyTypes();
    throw error;
  }

  return loadMensDutyTypes(userId);
}

export async function createMensDutyType(userId, name = "", existingTypes = []) {
  const safeName = String(name || "").trim();
  if (!safeName) throw new Error("Add a duty name.");

  const nextSortOrder = Math.max(0, ...(existingTypes || []).map((item) => Number(item.sortOrder || 0))) + 1;

  if (!userId) {
    return saveLocalDutyTypes([
      ...readLocalDutyTypes(),
      normalizeDutyType({ name: safeName, sortOrder: nextSortOrder }, nextSortOrder),
    ]);
  }

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    return saveLocalDutyTypes([
      ...readLocalDutyTypes(),
      normalizeDutyType({ name: safeName, sortOrder: nextSortOrder }, nextSortOrder),
    ]);
  }

  const { error } = await supabase.from(MENS_DUTY_TYPES_TABLE).insert({
    account_id: accountId,
    name: safeName,
    sort_order: nextSortOrder,
  });

  if (error) throw error;
  return loadMensDutyTypes(userId);
}

export async function updateMensDutyType(userId, dutyTypeId, values = {}) {
  const safeName = String(values.name || "").trim();
  if (!safeName) throw new Error("Add a duty name.");

  if (!userId || !dutyTypeId) {
    return saveLocalDutyTypes(
      readLocalDutyTypes().map((item) =>
        item.id === dutyTypeId ? { ...item, name: safeName } : item
      )
    );
  }

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    return saveLocalDutyTypes(
      readLocalDutyTypes().map((item) =>
        item.id === dutyTypeId ? { ...item, name: safeName } : item
      )
    );
  }

  const { error } = await supabase
    .from(MENS_DUTY_TYPES_TABLE)
    .update({
      name: safeName,
      updated_at: new Date().toISOString(),
    })
    .eq("account_id", accountId)
    .eq("id", dutyTypeId);

  if (error) throw error;
  return loadMensDutyTypes(userId);
}

export async function reorderMensDutyTypes(userId, dutyTypes = []) {
  const orderedTypes = dutyTypes.map((item, index) => ({
    ...item,
    sortOrder: index + 1,
  }));

  if (!userId) {
    return saveLocalDutyTypes(orderedTypes);
  }

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    return saveLocalDutyTypes(orderedTypes);
  }

  const updates = orderedTypes
    .filter((item) => item?.id)
    .map((item) =>
      supabase
        .from(MENS_DUTY_TYPES_TABLE)
        .update({
          sort_order: item.sortOrder,
          updated_at: new Date().toISOString(),
        })
        .eq("account_id", accountId)
        .eq("id", item.id)
    );

  const results = await Promise.all(updates);
  const failed = results.find((result) => result.error);
  if (failed?.error) throw failed.error;

  return loadMensDutyTypes(userId);
}

export async function deleteMensDutyType(userId, dutyTypeId) {
  if (!dutyTypeId) return readLocalDutyTypes();

  if (!userId) {
    return saveLocalDutyTypes(readLocalDutyTypes().filter((item) => item.id !== dutyTypeId));
  }

  const accountId = await getChurchAccountId(userId);
  if (!accountId) {
    return saveLocalDutyTypes(readLocalDutyTypes().filter((item) => item.id !== dutyTypeId));
  }

  const { error } = await supabase
    .from(MENS_DUTY_TYPES_TABLE)
    .delete()
    .eq("account_id", accountId)
    .eq("id", dutyTypeId);

  if (error) throw error;
  return loadMensDutyTypes(userId);
}
